// Where a tab keeps its working state (2026-10-08 bug sweep).
//
// One shared localStorage entry let tabs overwrite each other (TG-1: an idle
// tab's old "polling" Tagger job replaced another tab's finish). Pure
// sessionStorage fixes that but dies with the tab — and polling drives
// generation here (a Fabric swap doesn't start, a photoshoot isn't collected
// or saved, until a status poll runs), so closing a tab mid-run lost a paid
// result. So each tab writes its own localStorage keys, and a NEW tab adopts
// the work of a tab that is gone — the jobs (redux-persist) AND each tool's
// set-up records (`tabStorage`, a drop-in for sessionStorage), together.
//
//   refresh            same tab id (sessionStorage) → its own keys, exactly
//   two open tabs      separate keys → never overwrite each other
//   tab closed mid-run the next new tab adopts its keys (heartbeat gone):
//                      polling resumes and the tool's set-up comes back too
//   duplicated tab     the copy sees the original's live heartbeat under the
//                      same id → takes a new id and starts fresh
//   old single key     `persist:root` from older builds is adopted once

const PREFIX = "persist:root:";
const LEGACY = "persist:root";
const DATA = "tabdata:";
const ALIVE = "tabs:alive:";
const ID_KEY = "tabs:id";
const HEARTBEAT_MS = 5000;
// Background tabs get their timers throttled to about once a minute, so a tab
// counts as gone only well past that (a normal close or refresh removes its beat at once).
const GONE_AFTER_MS = 3 * 60 * 1000;
const FORGET_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
const TERMINAL = new Set(["succeeded", "failed", "idle", "cancelled", "canceled", undefined, null]);

const newId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const safe = (fn, fallback = null) => { try { return fn(); } catch (e) { return fallback; } };
const keys = () => safe(() => Object.keys(localStorage), []);

export function hasRunningJob(serialized) {
  const jobs = safe(() => JSON.parse(JSON.parse(serialized).jobs), null);
  return !!jobs && Object.values(jobs).some((j) => j && typeof j === "object" && !TERMINAL.has(j.status));
}

function isAlive(id, now = Date.now()) {
  const beat = Number(safe(() => localStorage.getItem(ALIVE + id), 0));
  return !!beat && now - beat < GONE_AFTER_MS;
}

let current = null;
/** This tab's id (synchronous). A duplicated tab copies sessionStorage, so an id whose heartbeat is still live belongs to another open tab. */
export function tabId() {
  if (current) return current;
  let id = safe(() => sessionStorage.getItem(ID_KEY));
  if (id && isAlive(id)) id = null; // the original tab is open: this is a copy
  if (!id) {
    id = newId();
    safe(() => sessionStorage.setItem(ID_KEY, id));
  }
  current = id;
  const beat = () => safe(() => localStorage.setItem(ALIVE + id, String(Date.now())));
  beat();
  if (typeof window !== "undefined") {
    setInterval(beat, HEARTBEAT_MS);
    window.addEventListener("pagehide", () => safe(() => localStorage.removeItem(ALIVE + id)));
    window.addEventListener("pageshow", beat);
  }
  return id;
}

// Older builds' single key has no tab owner, and an old Tagger job there can be
// a stale "polling" copy of a run that already finished (TG-1) with no run id
// to make a re-run safe — never resume it.
function withoutUnsafeLegacyJobs(value) {
  return safe(() => {
    const root = JSON.parse(value);
    const jobs = JSON.parse(root.jobs);
    if (jobs?.productTagger && !TERMINAL.has(jobs.productTagger.status)) delete jobs.productTagger;
    return JSON.stringify({ ...root, jobs: JSON.stringify(jobs) });
  }, value);
}

const dataPrefix = (id) => `${DATA}${id}:`;
const dataKeysOf = (id) => keys().filter((k) => k.startsWith(dataPrefix(id)));

function forgetTab(id) {
  safe(() => localStorage.removeItem(PREFIX + id));
  for (const k of dataKeysOf(id)) safe(() => localStorage.removeItem(k));
  safe(() => localStorage.removeItem(ALIVE + id));
}

/** Move a gone tab's keys (jobs + set-up records) to this tab. */
function moveTab(from, to) {
  const value = safe(() => localStorage.getItem(PREFIX + from));
  if (value) safe(() => localStorage.setItem(PREFIX + to, value));
  for (const k of dataKeysOf(from)) {
    const v = safe(() => localStorage.getItem(k));
    if (v != null) safe(() => localStorage.setItem(dataPrefix(to) + k.slice(dataPrefix(from).length), v));
  }
  forgetTab(from);
}

let claimed = false;
/** Once per page load, before anything reads: a fresh tab adopts the newest unfinished work of a tab that is gone. */
export function claimOrphanWork(now = Date.now()) {
  if (claimed) return;
  claimed = true;
  const me = tabId();
  if (safe(() => localStorage.getItem(PREFIX + me)) || dataKeysOf(me).length) return; // a refresh: our own work is here
  const owners = new Set();
  for (const key of keys()) {
    if (key === LEGACY) owners.add("legacy");
    else if (key.startsWith(PREFIX)) owners.add(key.slice(PREFIX.length));
    else if (key.startsWith(DATA)) owners.add(key.slice(DATA.length).split(":")[0]);
  }
  owners.delete(me);
  const candidates = [];
  for (const owner of owners) {
    if (!owner || (owner !== "legacy" && isAlive(owner, now))) continue;
    let value = safe(() => localStorage.getItem(owner === "legacy" ? LEGACY : PREFIX + owner));
    if (owner === "legacy" && value) value = withoutUnsafeLegacyJobs(value);
    const at = owner === "legacy" ? 0
      : Math.max(Number(safe(() => JSON.parse(value)._at, 0)) || 0, Number(safe(() => localStorage.getItem(`${dataPrefix(owner)}__at`), 0)) || 0);
    if (!value || !hasRunningJob(value)) {
      // Nothing left to resume: forget it once it's old (the legacy key is kept for recovery).
      if (owner !== "legacy" && now - at > FORGET_AFTER_MS) forgetTab(owner);
      continue;
    }
    candidates.push({ owner, value, at });
  }
  candidates.sort((a, b) => b.at - a.at);
  const pick = candidates[0];
  if (!pick) return;
  if (pick.owner === "legacy") {
    safe(() => localStorage.setItem(PREFIX + me, pick.value));
    safe(() => localStorage.removeItem(LEGACY));
  } else {
    moveTab(pick.owner, me);
  }
}

// The write time lives in the stored record only (for adoption order); the app never sees it.
const unstamp = (value) => (value ? safe(() => { const { _at, ...rest } = JSON.parse(value); return JSON.stringify(rest); }, value) : value);

/** redux-persist storage: the app's single `persist:root` becomes one key per tab. */
export const tabWorkspaceStorage = {
  async getItem(key) {
    if (key !== LEGACY) return safe(() => localStorage.getItem(key));
    claimOrphanWork();
    return unstamp(safe(() => localStorage.getItem(PREFIX + tabId())));
  },
  async setItem(key, value) {
    if (key !== LEGACY) return safe(() => localStorage.setItem(key, value));
    // Stamp the write time so the newest orphan is adopted first.
    const stamped = safe(() => JSON.stringify({ ...JSON.parse(value), _at: String(Date.now()) }), value);
    localStorage.setItem(PREFIX + tabId(), stamped); // let quota errors reach writeFailHandler
  },
  async removeItem(key) {
    if (key !== LEGACY) return safe(() => localStorage.removeItem(key));
    safe(() => localStorage.removeItem(PREFIX + tabId()));
  },
};

/**
 * A tool's per-tab set-up records — a drop-in for sessionStorage that lives in
 * localStorage under this tab's id, so it survives the tab closing and moves to
 * the tab that adopts its unfinished job.
 */
export const tabStorage = {
  getItem(key) {
    claimOrphanWork();
    return safe(() => localStorage.getItem(dataPrefix(tabId()) + key));
  },
  setItem(key, value) {
    claimOrphanWork();
    const id = tabId();
    localStorage.setItem(dataPrefix(id) + key, String(value));
    safe(() => localStorage.setItem(`${dataPrefix(id)}__at`, String(Date.now())));
  },
  removeItem(key) {
    claimOrphanWork();
    safe(() => localStorage.removeItem(dataPrefix(tabId()) + key));
  },
};

// For tests.
export const _internals = { PREFIX, LEGACY, DATA, ALIVE, ID_KEY, GONE_AFTER_MS, reset: () => { current = null; claimed = false; } };
